
import { useState, useRef, useEffect } from 'react';
import Navigation from '../components/Navigation';

interface Message {
  id: string;
  type: 'user' | 'assistant';
  text: string;
  timestamp: Date;
  isVoice?: boolean;
}

const voiceCommands = [
  { icon: 'ri-volume-up-line', text: '今天天气怎么样？', category: 'info' },
  { icon: 'ri-calculator-line', text: '帮我算一下 25 加 37', category: 'math' },
  { icon: 'ri-book-line', text: '给我讲个故事', category: 'story' },
  { icon: 'ri-music-line', text: '唱首儿歌给我听', category: 'music' },
  { icon: 'ri-lightbulb-line', text: '为什么天空是蓝色的？', category: 'science' },
  { icon: 'ri-time-line', text: '现在几点了？', category: 'info' },
];

export default function VoiceAssistant() {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      type: 'assistant',
      text: '你好！我是你的语音助手小智，你可以用语音或文字和我交流哦！试试点击麦克风按钮说话吧！',
      timestamp: new Date(),
    }
  ]);
  const [inputText, setInputText] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleVoiceInput = () => {
    if (isProcessing) return; // Prevent voice input while processing
    
    setIsListening(true);
    
    // 模拟语音识别
    setTimeout(() => {
      setIsListening(false);
      const voiceTexts = [
        '今天天气怎么样？',
        '帮我算一下 10 加 20',
        '给我讲个故事',
        '为什么会下雨？',
        '唱首儿歌给我听'
      ];
      const randomText = voiceTexts[Math.floor(Math.random() * voiceTexts.length)];
      handleSendMessage(randomText, true);
    }, 2000);
  };

  const handleSendMessage = (text: string, isVoice = false) => {
    // Check if text exists and is not empty for both voice and text input
    if (!text || !text.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      text: text,
      timestamp: new Date(),
      isVoice,
    };

    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setIsProcessing(true);

    // 模拟AI回复
    setTimeout(() => {
      const responses: Record<string, string> = {
        '天气': '今天天气晴朗，温度大约25度，很适合户外活动哦！记得多喝水！',
        '算': `让我算算... ${text.includes('25') ? '25 + 37 = 62' : '10 + 20 = 30'}！你算对了吗？`,
        '故事': '从前有一只小兔子，它非常喜欢画画。有一天，它画了一幅美丽的彩虹，彩虹竟然变成了真的！小兔子沿着彩虹走啊走，遇到了很多新朋友...',
        '儿歌': '🎵 小星星，亮晶晶，满天都是小星星，挂在天上放光明，好像许多小眼睛... 🎵',
        '蓝色': '天空看起来是蓝色的，是因为阳光穿过大气层时，蓝色的光被散射得最多。就像你用手电筒照进水里，光会散开一样！',
        '下雨': '下雨是因为天上的云里有很多小水滴，当它们聚在一起变得太重了，就会掉下来，这就是雨啦！',
        '几点': `现在是 ${new Date().getHours()}:${String(new Date().getMinutes()).padStart(2, '0')}，记得按时吃饭和休息哦！`,
      };

      let response = '这是个很好的问题！让我想想... 你可以试试问我关于天气、数学、故事或科学的问题哦！';
      
      for (const [key, value] of Object.entries(responses)) {
        if (text.includes(key)) {
          response = value;
          break;
        }
      }

      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        text: response,
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, assistantMessage]);
      setIsProcessing(false);
    }, 1500);
  };

  const handleQuickCommand = (command: string) => {
    if (!isProcessing) {
      handleSendMessage(command);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-pink-50 to-purple-50">
      <Navigation />

      <div className="pt-24 pb-12 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-block bg-gradient-to-r from-orange-500 to-pink-500 p-4 rounded-3xl mb-4">
              <i className="ri-mic-line text-5xl text-white"></i>
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-3">语音助手小智</h1>
            <p className="text-lg text-gray-600">用声音和我交流，让学习更有趣！</p>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Quick Commands */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <i className="ri-command-line text-orange-500 mr-2"></i>
                  快速指令
                </h3>
                <div className="space-y-3">
                  {voiceCommands.map((cmd, index) => (
                    <button
                      key={index}
                      onClick={() => handleQuickCommand(cmd.text)}
                      className="w-full p-3 bg-gradient-to-r from-orange-50 to-pink-50 rounded-xl hover:from-orange-100 hover:to-pink-100 transition-all text-left flex items-center space-x-3 group cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={isProcessing}
                    >
                      <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                        <i className={`${cmd.icon} text-xl text-orange-500`}></i>
                      </div>
                      <span className="text-sm text-gray-700 flex-1">{cmd.text}</span>
                    </button>
                  ))}
                </div>

                <div className="mt-6 p-4 bg-blue-50 rounded-xl border-l-4 border-blue-500">
                  <div className="flex items-start space-x-2">
                    <i className="ri-lightbulb-line text-blue-600 text-lg mt-0.5"></i>
                    <div>
                      <p className="text-sm font-semibold text-gray-900 mb-1">使用提示</p>
                      <p className="text-xs text-gray-600 leading-relaxed">
                        点击麦克风按钮开始语音输入，或者直接输入文字。试试问我问题吧！
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Chat Area */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden flex flex-col" style={{ height: '600px' }}>
                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`flex items-start space-x-3 max-w-[80%] ${message.type === 'user' ? 'flex-row-reverse space-x-reverse' : ''}`}>
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                          message.type === 'user' 
                            ? 'bg-gradient-to-r from-orange-500 to-pink-500' 
                            : 'bg-gradient-to-r from-purple-500 to-blue-500'
                        }`}>
                          <i className={`${message.type === 'user' ? 'ri-user-line' : 'ri-robot-line'} text-white text-lg`}></i>
                        </div>
                        <div>
                          <div className={`rounded-2xl p-4 ${
                            message.type === 'user'
                              ? 'bg-gradient-to-r from-orange-500 to-pink-500 text-white'
                              : 'bg-gray-100 text-gray-900'
                          }`}>
                            {message.isVoice && (
                              <div className="flex items-center space-x-2 mb-2">
                                <i className="ri-mic-fill text-sm"></i>
                                <span className="text-xs opacity-90">语音输入</span>
                              </div>
                            )}
                            <p className="text-sm leading-relaxed">{message.text}</p>
                          </div>
                          <p className={`text-xs text-gray-400 mt-1 ${message.type === 'user' ? 'text-right' : ''}`}>
                            {message.timestamp.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {isProcessing && (
                    <div className="flex justify-start">
                      <div className="flex items-start space-x-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-r from-purple-500 to-blue-500 flex items-center justify-center">
                          <i className="ri-robot-line text-white text-lg"></i>
                        </div>
                        <div className="bg-gray-100 rounded-2xl p-4">
                          <div className="flex space-x-2">
                            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div ref={messagesEndRef} />
                </div>

                {/* Input Area */}
                <div className="border-t border-gray-200 p-4 bg-gray-50">
                  {isListening && (
                    <div className="mb-3 p-3 bg-orange-100 rounded-xl flex items-center justify-center space-x-3">
                      <div className="w-3 h-3 bg-orange-500 rounded-full animate-pulse"></div>
                      <span className="text-sm font-semibold text-orange-700">正在听你说话...</span>
                      <div className="w-3 h-3 bg-orange-500 rounded-full animate-pulse"></div>
                    </div>
                  )}
                  
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={handleVoiceInput}
                      disabled={isListening || isProcessing}
                      className={`w-12 h-12 rounded-full flex items-center justify-center transition-all cursor-pointer ${
                        isListening
                          ? 'bg-orange-500 animate-pulse'
                          : 'bg-gradient-to-r from-orange-500 to-pink-500 hover:shadow-lg'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      <i className={`${isListening ? 'ri-mic-fill' : 'ri-mic-line'} text-white text-xl`}></i>
                    </button>
                    
                    <input
                      type="text"
                      value={inputText}
                      onChange={(e) => setInputText(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && !isProcessing) {
                          handleSendMessage(inputText);
                        }
                      }}
                      placeholder="输入你想说的话..."
                      disabled={isProcessing}
                      className="flex-1 px-4 py-3 bg-white border-2 border-gray-200 rounded-xl focus:outline-none focus:border-orange-300 transition-all text-sm disabled:opacity-50"
                    />
                    
                    <button
                      onClick={() => handleSendMessage(inputText)}
                      disabled={!inputText.trim() || isProcessing}
                      className="px-6 py-3 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-xl font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                    >
                      发送
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
